import { useState } from 'react'
import { Head } from '@inertiajs/react'
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Button, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui'
import { StatusBadge } from '@/components/common/Badges'
import { EmptyState } from '@/components/common/EmptyState'
import { RowActions } from '@/components/common/RowActions'
import { MerchantFormModal } from '@/components/merchants/MerchantFormModal'
import { useTranslations } from '@/lib/i18n'

interface MerchantRow {
  id: number
  name: string
  status: string
  user_count: number
  has_credentials: boolean
}

interface AssignableUser {
  id: number
  name: string
  email: string
  role: string
}

function MerchantsIndex({
  merchants,
  statuses,
  assignable_users,
}: {
  merchants: MerchantRow[]
  statuses: string[]
  assignable_users: AssignableUser[]
}) {
  const { t } = useTranslations()
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <>
      <Head title={t('merchants.title')} />
      <PageHeading
        title={t('merchants.title')}
        subtitle={t('merchants.subtitle')}
        actions={
          <Button color="brand" onClick={() => setModalOpen(true)}>
            {t('merchants.add')}
          </Button>
        }
      />

      <MerchantFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        statuses={statuses}
        assignableUsers={assignable_users}
      />

      {merchants.length === 0 ? (
        <EmptyState
          className="mt-8"
          icon={BuildingStorefrontIcon}
          title={t('merchants.empty')}
          action={
            <Button color="brand" onClick={() => setModalOpen(true)}>
              {t('merchants.add')}
            </Button>
          }
        />
      ) : (
        <Table className="mt-8">
          <TableHead>
            <TableRow>
              <TableHeader>{t('merchants.name')}</TableHeader>
              <TableHeader>{t('merchants.status')}</TableHeader>
              <TableHeader>{t('merchants.assigned_users')}</TableHeader>
              <TableHeader>{t('merchants.credentials_label')}</TableHeader>
              <TableHeader className="text-right">{t('common.actions')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {merchants.map((merchant) => (
              <TableRow key={merchant.id}>
                <TableCell className="font-medium text-text-strong">{merchant.name}</TableCell>
                <TableCell>
                  <StatusBadge on={merchant.status === 'active'} label={t(`statuses.${merchant.status}`)} />
                </TableCell>
                <TableCell>{t('merchants.users_count', { count: merchant.user_count })}</TableCell>
                <TableCell>
                  <StatusBadge
                    on={merchant.has_credentials}
                    label={merchant.has_credentials ? t('merchants.configured') : t('merchants.not_configured')}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <RowActions
                    menuLabel={t('common.actions')}
                    items={[{ label: t('common.edit'), href: `/merchants/${merchant.id}/edit` }]}
                    destroy={{
                      url: `/merchants/${merchant.id}`,
                      label: t('common.delete'),
                      confirm: t('merchants.delete_confirm'),
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}

MerchantsIndex.layout = withAppLayout

export default MerchantsIndex
